'use client';

import { Children, ReactNode, useMemo, useState } from 'react';
import DynamicMarquee from '@/components/DynamicMarquee';

// Same edge fade as the HeroVelxEngine redesign logo ribbon — feathers the
// row's left/right edges so cards slide in and out of view cleanly.
const CARDS_MASK: React.CSSProperties = {
  WebkitMaskImage:
    'linear-gradient(to right, transparent 0, black 24px, black calc(100% - 24px), transparent 100%)',
  maskImage:
    'linear-gradient(to right, transparent 0, black 24px, black calc(100% - 24px), transparent 100%)',
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat',
};

type Props = {
  children: ReactNode;
  /**
   * Marquee scroll speed (pixels per second). Matches the value the
   * HeroVelxEngine logo ribbon uses so the feel is consistent across the site.
   */
  speed?: number;
  /**
   * Gap between adjacent cards in pixels. Applied through a right margin on
   * every card cell so it survives the array doubling below.
   */
  gap?: number;
};

/**
 * Auto-scrolling row of cards. Mirrors the HeroVelxEngine redesign logo
 * ribbon UX: continuous marquee, pauses on hover, respects
 * `prefers-reduced-motion` (handled by `DynamicMarquee`), and a soft edge fade
 * so cards enter and leave the strip without a hard cut.
 *
 * The children are duplicated so one full pass always overflows the container
 * — otherwise the track would show a gap between the last and first card at
 * every loop. Cells stay `shrink-0` so each card keeps whatever width it sets
 * itself (variations decide the "~1/3 viewport" sizing on the card, not here).
 */
export function MarqueeCardsRow({ children, speed = 30, gap = 24 }: Props) {
  const items = useMemo(() => Children.toArray(children), [children]);
  const doubled = useMemo(() => [...items, ...items], [items]);
  const [isPaused, setIsPaused] = useState(false);

  if (items.length === 0) {
    return null;
  }

  // `react-fast-marquee` centers its rows and children by default
  // (`align-items: center` on both `.rfm-marquee` and
  // `.rfm-initial-child-container`), which lets short cards float in a taller
  // row instead of matching heights. Overriding to `stretch` here — plus
  // `h-full` on the cells and cards — is what makes every card in the row
  // adopt the height of the tallest one.
  return (
    <div
      className="relative w-full [&_.rfm-initial-child-container]:items-stretch [&_.rfm-marquee]:items-stretch"
      style={CARDS_MASK}
    >
      <DynamicMarquee speed={speed} gradient={false} play={!isPaused}>
        {doubled.map((child, index) => (
          <div
            key={`marquee-card-${index}`}
            className="flex h-full shrink-0 items-stretch"
            style={{ marginRight: gap }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {child}
          </div>
        ))}
      </DynamicMarquee>
    </div>
  );
}
